import { FC, useContext, useEffect } from 'react'
import styled from 'styled-components'
import ActionButton from 'components/ActionButton'
import {
  ModalActions,
  ModalCard,
  ModalMessage,
  ModalOverlay,
  ModalTitle
} from 'components/modals/styles'
import { setAssessments } from 'app/reducer/assessmentSlice'
import { QuestionnaireContext } from '../QuestionnaireContext'
import { useDispatch, useSelector } from 'react-redux'
import { isSubmitApiErrorSelector } from 'app/reducer/errorSlice'

const Overlay = styled(ModalOverlay)`
  z-index: 220;
`

const Card = styled(ModalCard)`
  width: 500px;
  max-width: 500px;
  height: 147px;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`

const Header = styled.div``

const Body = styled.div`
  margin-top: 4px;
`

const Footer = styled(ModalActions)`
  margin-top: 8px;
`

type FinishModalProps = {
  show?: boolean
  setShow?: (show: boolean) => void
  questionCompleted?: number
  totalQuestions?: number
  onFinish?: () => void
  onReturnToTest?: () => void
  submitLoading?: boolean
}

const FinishModal: FC<FinishModalProps> = ({
  show,
  setShow,
  questionCompleted: questionCompletedOverride,
  totalQuestions,
  onFinish,
  onReturnToTest,
  submitLoading
}) => {
  const {
    showFinishModal,
    setShowFinishModal,
    questions,
    onSubmitAssessment,
    setQuestionIdx,
    saveExpressionAndMarkings,
    submitTestLoading
  } = useContext(QuestionnaireContext)

  const isSubmitApiError = useSelector(isSubmitApiErrorSelector)

  const questionCompleted =
    questionCompletedOverride ??
    questions.reduce(
      (a, b) => (b.answer !== undefined && b.answer !== null ? a + 1 : a),
      0
    )
  const questionCount = totalQuestions ?? questions.length
  const resolvedShowFinishModal = show ?? showFinishModal
  const setResolvedShowFinishModal = setShow ?? setShowFinishModal
  const resolvedSubmitLoading = submitLoading ?? submitTestLoading
  const dispatch = useDispatch()

  useEffect(() => {
    if (isSubmitApiError && resolvedShowFinishModal) {
      setResolvedShowFinishModal(false)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubmitApiError, resolvedShowFinishModal, setResolvedShowFinishModal])

  useEffect(() => {
    const handlePress = (event: any) => {
      if (event.keyCode === 27) {
        setResolvedShowFinishModal(false)
      }
    }

    !isSubmitApiError &&
      resolvedShowFinishModal &&
      document.addEventListener('keydown', handlePress, false)

    return () => {
      document.removeEventListener('keydown', handlePress, false)
    }
  }, [resolvedShowFinishModal, isSubmitApiError, setResolvedShowFinishModal])

  const hasCompletedQuestions = questionCompleted > 0

  const onReturnToTestClick = () => {
    setResolvedShowFinishModal(false)

    if (onReturnToTest) {
      onReturnToTest()
      return
    }

    const unAnswered = questions.findIndex(
      (question) => question.answer === undefined || question.answer === null
    )
    if (unAnswered >= 0) {
      saveExpressionAndMarkings()
      setQuestionIdx(unAnswered)
    }
  }

  const onFinishAssessment = () => {
    if (onFinish) {
      onFinish()
      return
    }

    onSubmitAssessment()
    dispatch(setAssessments([]))
  }

  const onOverlayClick = () => {
    setResolvedShowFinishModal(false)
  }

  return resolvedShowFinishModal ? (
    <Overlay onClick={onOverlayClick}>
      <Card
        role='dialog'
        aria-modal='true'
        aria-labelledby='finish-modal-title'
        onClick={(event) => event.stopPropagation()}
      >
        <Header>
          <ModalTitle id='finish-modal-title'>
            {`You have completed: ${questionCompleted} out of ${questionCount} question(s).`}
          </ModalTitle>
        </Header>
        <Body>
          <ModalMessage>
            Select "Return to Test" to review or "Finish" to submit test.
          </ModalMessage>
        </Body>
        <Footer>
          <ActionButton
            label='Return To Test'
            variant='clear'
            width={128}
            onClick={onReturnToTestClick}
          />
          <ActionButton
            label='Finish'
            variant='error'
            width={92}
            onClick={onFinishAssessment}
            disabled={resolvedSubmitLoading || !hasCompletedQuestions}
            loading={resolvedSubmitLoading}
          />
        </Footer>
      </Card>
    </Overlay>
  ) : null
}

export default FinishModal
